import { TabContext, TabPanel } from '@mui/lab';
import { Stack, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useContext, useEffect, useState } from 'react';
import '../../App.css';
import { StyleConstants, Styles } from '../../Styles';
import SnackbarContext from '../../components/SnackbarContext';
import { API_CALL_INTERVAL } from '../../config/Config';
import RestfulManager, { RES_STATUS_OK } from '../../manager/RestfulManager';
import RawBoard1Tab from './RawBoard1Tab';
import RawBoard2Tab from './RawBoard2Tab';
import RawBoard3Tab from './RawBoard3Tab';
import RawData2Tab from './RawData2Tab';
import SessionManager from '../../manager/SessionManager';
import useInterval from '../../hook/useInterval';

const RawPage = () => {
    const { setOpenSnackBar, setSnackbarSeverity, setSnackbarMessage } = useContext(SnackbarContext);
    
    const sessionManager = new SessionManager();
    const restfulManager = new RestfulManager();

    const [cars, setCars] = useState([]);
    const [device, setDevice] = useState({});
    const [packet, setPacket] = useState({});

    const [rawPageCarCode, setRawPageCarCode] = useState(sessionManager.getRawPageCarCode() !== null ? sessionManager.getRawPageCarCode() : '');

    const [boardTab, setBoardTab] = useState(0);

    const loadDeviceData = (carCode) => {
        (async () => {
            try {
                if (carCode) {
                    let selectedCar = null;
                    for (let i = 0; i < cars.length; i += 1) {
                        if (cars[i].carCode === carCode) {
                            selectedCar = cars[i];
                            break;
                        }
                    }

                    const res = await restfulManager.getDeviceByDeviceId(sessionManager.getAccessToken(), selectedCar.deviceId);
                    const json = await res.json();
                    
                    if ((res.status === 200) && (json.status === RES_STATUS_OK)) {
                        setDevice(json.device);
                    } else {
                        setDevice({});

                        if (json && json.msg) {
                            setSnackbarSeverity('error');
                            setSnackbarMessage(json.msg);
                            setOpenSnackBar(true);
                        }
                    }
                }
            } catch (e) {
                console.error(e);
                setSnackbarSeverity('error');
                setSnackbarMessage('Error. Please contact administrator.');
                setOpenSnackBar(true);
            }
        })();
    };

    const loadRawData = (carCode) => {
        (async () => {
            try {
                if (carCode) {
                    const res = await restfulManager.getLatestPacketByCarCode(sessionManager.getAccessToken(), carCode);
                    const json = await res.json();

                    if ((res.status === 200) && (json.status === RES_STATUS_OK)) {
                        setPacket(json.decodedPacket);
                    } else {
                        setPacket({});

                        if (json && json.msg) {
                            setSnackbarSeverity('error');
                            setSnackbarMessage(json.msg);
                            setOpenSnackBar(true);
                        }
                    }
                }
            } catch (e) {
                console.error(e);
                setSnackbarSeverity('error');
                setSnackbarMessage('Error. Please contact administrator.');
                setOpenSnackBar(true);
            }
        })();
    };

    const carSelectChange = (event) => {
        const selectedCarCode = event.target.value;
        setRawPageCarCode(selectedCarCode);
        sessionManager.setRawPageCarCode(selectedCarCode);
    };

    const boardTabChange = (event, newValue) => {
        setBoardTab(newValue);
    };

    useEffect(() => {
        (async () => {
            try {
                const res = await restfulManager.getCars(sessionManager.getAccessToken());
                const json = await res.json();
                if ((res.status === 200) && (json.status === RES_STATUS_OK)) {
                    setCars(json.cars);
                } else {
                    if (json && json.msg) {
                        setSnackbarSeverity('error');
                        setSnackbarMessage(json.msg);
                        setOpenSnackBar(true);
                    }
                }
            } catch (e) {
                console.error(e);
                setSnackbarSeverity('error');
                setSnackbarMessage('Error. Please contact administrator.');
                setOpenSnackBar(true);
            }
        })();
    }, []);

    useInterval(() => {
        loadDeviceData(rawPageCarCode);
        loadRawData(rawPageCarCode);
    }, API_CALL_INTERVAL, [rawPageCarCode]);

    return (
        <>
            <Box sx={{ flexGrow: 1, padding: '8px' }}>
                <Stack direction="column">
                    <Stack direction="row" alignItems="center">
                        <Typography sx={{ ...Styles.contentFont3, marginRight: '8px' }}>
                            Car Number:
                        </Typography>
                        <Select
                            value={rawPageCarCode}
                            onChange={carSelectChange}
                            sx={Styles.carSelect}
                            MenuProps={{
                                sx: {
                                    "& .MuiMenuItem-root.Mui-selected": {
                                        color: 'red',
                                        backgroundColor: StyleConstants.mainBgColor,
                                    },
                                    "& .MuiMenuItem-root:hover": {
                                        backgroundColor: StyleConstants.highlightBgColor,
                                    },
                                    "& .MuiMenuItem-root.Mui-selected:hover": {
                                        backgroundColor: StyleConstants.highlightBgColor,
                                    }
                                }
                            }}
                        >
                            {cars.map((car) => (
                                <MenuItem
                                    key={car.carCode}
                                    value={car.carCode}
                                    sx={Styles.carSelect}
                                >
                                    <Typography sx={{ fontSize: Styles.contentFont4 }}>
                                        {car.carCode}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Select>

                        <Typography sx={{ ...Styles.contentFont3, marginLeft: '24px' }}>
                            Patch Version: {device && device.patchVer}
                        </Typography>
                        <Typography sx={{ ...Styles.contentFont3, marginLeft: '48px' }}>
                            DAU Version: {device && device.dauVer}
                        </Typography>
                        <Typography sx={{ ...Styles.contentFont3, marginLeft: '48px' }}>
                            DAUGW Version: {device && device.daugwVer}
                        </Typography>
                    </Stack>

                    <Stack direction="column" sx={{ marginTop: '8px' }}>
                        {JSON.stringify(packet) !== JSON.stringify({}) && (
                            <>
                                <Box sx={{ color: StyleConstants.fontColor }}>
                                    Packet Date Time: {packet.dateTime}
                                </Box>
                                <TabContext value={String(boardTab)}>
                                    <Tabs
                                        value={boardTab}
                                        onChange={boardTabChange}
                                        sx={{
                                            marginTop: '8px',
                                            backgroundColor: StyleConstants.componentBgColor,
                                        }}
                                        TabIndicatorProps={{
                                            style: {
                                            backgroundColor: "red"
                                            }
                                        }}
                                    >
                                        <Tab
                                            label={<span style={{ color: '#FFFFFF' }}>Board 1</span>}
                                            value={0}
                                        />
                                        <Tab
                                            label={<span style={{ color: '#FFFFFF' }}>Board 2</span>}
                                            value={1}
                                        />
                                        <Tab
                                            label={<span style={{ color: '#FFFFFF' }}>Board 3</span>}
                                            value={2}
                                        />
                                        <Tab
                                            label={<span style={{ color: '#FFFFFF' }}>Data 2</span>}
                                            value={3}
                                        />
                                    </Tabs>

                                    <Box sx={{ width: '1835px', height: '861px', overflow: 'scroll', border: '3px solid ' + StyleConstants.borderColor, backgroundColor: StyleConstants.componentBgColor, color: StyleConstants.fontColor, marginTop: '4px' }}>
                                        <TabPanel value={'0'} sx={{ height: '100%' }}>
                                            <RawBoard1Tab packet={packet} />
                                        </TabPanel>
                                        <TabPanel value={'1'} sx={{ height: '100%' }}>
                                            <RawBoard2Tab packet={packet} />
                                        </TabPanel>
                                        <TabPanel value={'2'} sx={{ height: '100%' }}>
                                            <RawBoard3Tab packet={packet} />
                                        </TabPanel>
                                        <TabPanel value={'3'} sx={{ height: '100%' }}>
                                            <RawData2Tab packet={packet} />
                                        </TabPanel>
                                    </Box>
                                </TabContext>
                            </>
                        )}
                    </Stack>
                </Stack>
            </Box>
        </>
    );
};

export default RawPage;